import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ChartWithDetailedTooltip from "@client.components/Charts/ChartWithDetailedTooltip";
import { filterPropTypes } from "@client.components/Charts/utils";
import { CHART_CLASS } from "./CustomXAxisTick";
import {
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Cell,
  ResponsiveContainer,
} from "recharts";
import CustomXAxisTick from "./CustomXAxisTick";
import { XAXIS_PADDING } from "./CustomXAxisTick";
import variables from "@client.scss.variables";
import Legend from "./Legend";

const NumberOfInsights = ({ onError, chartData }) => {
  const { t } = useTranslation();
  const total = useMemo(() => {
    return chartData.data.reduce((acc, r) => acc + r.count, 0);
  }, [chartData]);

  const legendItems = useMemo(() => {
    const { data, maxCount } = chartData;
    const items = [];
    if (maxCount > 0) {
      items.push({
        title: t("pages.Dashboard.Insights.legend.high"),
        color: variables.chartColor3,
      });
    }
    for (let record of data) {
      if (record.count < maxCount) {
        items.unshift({
          title: t("pages.Dashboard.Insights.legend.normal"),
          color: variables.chartColor2,
        });
        break;
      }
    }
    return items;
  }, [chartData, t]);

  return (
    <ChartWithDetailedTooltip
      title={t("pages.Dashboard.Insights.numberOfInsights", {
        value: total,
      })}
      className={CHART_CLASS}
      dataSource={chartData}
      onError={onError}
      isHorizontal={false}
      renderRightComponent={() => <Legend items={legendItems} />}
      getTooltipContent={(payload) =>
        t("pages.Dashboard.Insights.tooltip", {
          value: payload.count,
          type: payload.typeDescription,
        })
      }
      renderChartContent={(
        handleBarMouseEnter,
        handleBarMouseMove,
        handleBarMouseLeave
      ) => {
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData.data}
              margin={{
                top: 20,
                right: 0,
                left: 0,
                bottom: 10,
              }}
              barSize={20}
            >
              <XAxis
                dataKey="typeDescription"
                className="top-meeting-participants-xaxis"
                padding={XAXIS_PADDING}
                axisLine={false}
                tick={<CustomXAxisTick barQty={chartData.data.length} />}
                interval={0}
              />
              <YAxis dataKey="count" axisLine={false} />
              <Bar
                dataKey="count"
                radius={[9, 9, 9, 9]}
                onMouseMove={handleBarMouseMove}
                onMouseEnter={handleBarMouseEnter}
                onMouseLeave={handleBarMouseLeave}
              >
                {chartData.data.map((row, index) => (
                  <Cell
                    key={`tmp-${index}`}
                    fill={
                      chartData.maxCount === row.count
                        ? variables.chartColor3
                        : variables.chartColor2
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );
      }}
    />
  );
};

NumberOfInsights.propTypes = {
  onError: PropTypes.func.isRequired,
  filter: filterPropTypes,
  chartData: PropTypes.shape({
    data: PropTypes.array,
    maxCount: PropTypes.number,
  }),
};

export default NumberOfInsights;
